/**
 * Urdu -> Roman Urdu dictionary for the transliteration layer.
 *
 * Every entry here is a word the rule engine would otherwise get wrong, because
 * Urdu script omits short vowels: the rules see کتاب as k-t-aa-b and cannot know
 * that a reader supplies "kitaab". So the dictionary carries the vowels and the
 * rules only handle whatever falls through.
 *
 * Roman spellings follow the most common conventions in Pakistani usage
 * (`kaisay`, `bohat`, `mohabbat`) rather than a strict academic scheme, since the
 * output is meant for slugs, search keys and human readers.
 *
 * The reverse direction is derived from this map, so a duplicate Roman value
 * means the later Urdu word wins as the reverse target. Where that matters, the
 * preferred reverse form is pinned explicitly in `transliterate.ts` or `ROMAN_VARIANTS`.
 */
export const WORD_DICTIONARY: Record<string, string> = {
  // ---- pronouns, determiners, question words ----
  "میں": "mein",
  "ہم": "hum",
  "تم": "tum",
  "آپ": "aap",
  "وہ": "woh",
  "یہ": "yeh",
  "میرا": "mera",
  "میری": "meri",
  "میرے": "mere",
  "ہمارا": "hamara",
  "ہماری": "hamari",
  "ہمارے": "hamaray",
  "تمہارا": "tumhara",
  "تمہاری": "tumhari",
  "تمہارے": "tumharay",
  "آپکا": "aapka",
  "آپکی": "aapki",
  "آپکے": "aapke",
  "اس": "is",
  "اسکا": "iska",
  "اسکی": "iski",
  "اسکے": "iske",
  "ان": "un",
  "انکا": "unka",
  "انکی": "unki",
  "انکے": "unke",
  "مجھے": "mujhe",
  "ہمیں": "humein",
  "تمہیں": "tumhein",
  "انہیں": "unhein",
  "اسے": "isay",
  "جسے": "jisay",
  "کسے": "kisay",
  "کس": "kis",
  "کسکا": "kiska",
  "کسکی": "kiski",
  "کسکے": "kiske",
  "کسکو": "kisko",
  "جن": "jin",
  "جنکا": "jinka",
  "جنکی": "jinki",
  "جنکے": "jinke",
  "جنہوں": "jinhon",
  "انہوں": "unhon",
  "اپنا": "apna",
  "اپنی": "apni",
  "اپنے": "apnay",
  "خود": "khud",
  "کون": "kaun",
  "کیا": "kya",
  "کہاں": "kahan",
  "کدھر": "kidhar",
  "ادھر": "idhar",
  "جدھر": "jidhar",
  "کب": "kab",
  "کیوں": "kyun",
  "کیسے": "kaisay",
  "کیسا": "kaisa",
  "کیسی": "kaisi",
  "کتنا": "kitna",
  "کتنی": "kitni",
  "کتنے": "kitnay",
  "جو": "jo",
  "جس": "jis",
  "جسکو": "jisko",
  "جسکا": "jiska",
  "جسکی": "jiski",
  "جسکے": "jiske",
  "سب": "sab",
  "سبھی": "sabhi",
  "کوئی": "koi",
  "کچھ": "kuch",
  "ہر": "har",
  "ایک": "aik",

  // ---- function words, prepositions, conjunctions ----
  "اور": "aur",
  "یا": "ya",
  "اگر": "agar",
  "لیکن": "lekin",
  "مگر": "magar",
  "تو": "to",
  "بھی": "bhi",
  "نہ": "na",
  "نہیں": "nahi",
  "مت": "mat",
  "کا": "ka",
  "کی": "ki",
  "کے": "ke",
  "کو": "ko",
  "نے": "ne",
  "سے": "se",
  "پر": "par",
  "تک": "tak",
  "لیے": "liye",
  "ساتھ": "sath",
  "بغیر": "baghair",
  "وجہ": "wajah",
  "طرح": "tarah",
  "طرف": "taraf",
  "بارے": "baray",
  "بعد": "baad",
  "پہلے": "pehlay",
  "دوران": "doran",
  "درمیان": "darmiyan",
  "علاوہ": "alawa",
  "سوائے": "siwaye",
  "سوا": "siwa",
  "مطابق": "mutabiq",
  "باعث": "baais",
  "ذریعے": "zariye",
  "تحت": "tehat",
  "خلاف": "khilaf",
  "حق": "haq",
  "حتی": "hatta",
  "گویا": "goya",
  "چنانچہ": "chunancha",
  "خواہ": "khwah",
  "چاہے": "chahe",
  "بلکہ": "balkeh",
  "حالانکہ": "halankeh",
  "چونکہ": "chunkeh",
  "کیونکہ": "kyunkeh",
  "تاکہ": "taakeh",
  "ورنہ": "warna",
  "البتہ": "albatta",
  "مثلاً": "maslan",
  "یعنی": "yaani",
  "خصوصاً": "khasosan",
  "عموماً": "amuman",
  "فوراً": "fauran",
  "دوبارہ": "dobara",
  "یقیناً": "yaqeenan",
  "تقریباً": "taqreeban",
  "بعض": "baaz",
  "تمام": "tamam",
  "پورا": "poora",
  "پوری": "poori",
  "پورے": "pooray",
  "آدھا": "aadha",
  "دونوں": "donon",
  "تینوں": "teenon",
  "چاروں": "charon",
  "سارا": "sara",
  "ساری": "sari",
  "سارے": "saray",
  "باقی": "baqi",

  // ---- place and direction ----
  "یہاں": "yahan",
  "وہاں": "wahan",
  "کہیں": "kahin",
  "وہیں": "wahin",
  "یہیں": "yahin",
  "سامنے": "samnay",
  "پیچھے": "peechay",
  "آگے": "agay",
  "اوپر": "oopar",
  "نیچے": "neechay",
  "اندر": "andar",
  "باہر": "bahar",
  "پاس": "paas",
  "دور": "door",
  "قریب": "qareeb",
  "دائیں": "dayen",
  "بائیں": "bayen",
  "سیدھا": "seedha",
  "الٹا": "ulta",
  "شمال": "shumal",
  "جنوب": "janoob",
  "مشرق": "mashriq",
  "مغرب": "maghrib",
  "وسط": "wast",

  // ---- to be, and tense auxiliaries ----
  "ہے": "hai",
  "ہیں": "hain",
  "ہوں": "hoon",
  "ہو": "ho",
  "تھا": "tha",
  "تھی": "thi",
  "تھے": "thay",
  "تھیں": "thein",
  "ہوگا": "hoga",
  "ہوگی": "hogi",
  "ہونگے": "honge",
  "ہوںگے": "honge",
  "ہونا": "hona",
  "ہوا": "hua",
  "ہوئی": "hui",
  "ہوئے": "hue",

  // ---- common verbs & inflections ----
  "کرنا": "karna",
  "کرتا": "karta",
  "کرتی": "karti",
  "کرتے": "karte",
  "کریں": "karain",
  "کرو": "karo",
  "کر": "kar",
  "کرنے": "karne",
  "کیے": "kiye",
  "جانا": "jana",
  "جاتا": "jata",
  "جاتی": "jati",
  "جاتے": "jate",
  "جائے": "jaye",
  "جائیں": "jayen",
  "جاؤ": "jao",
  "جا": "ja",
  "جانے": "jane",
  "گیا": "gaya",
  "گئی": "gayi",
  "گئے": "gaye",
  "آنا": "aana",
  "آتا": "aata",
  "آتی": "aati",
  "آتے": "aate",
  "آئے": "aaye",
  "آئیں": "aayen",
  "آیا": "aaya",
  "آئی": "aayi",
  "آنے": "aane",
  "آؤ": "aao",
  "آ": "aa",
  "دینا": "dena",
  "دیتا": "deta",
  "دیتی": "deti",
  "دیتے": "dete",
  "دیا": "diya",
  "دیے": "diye",
  "دے": "de",
  "دو": "do",
  "دیں": "dain",
  "دینے": "dene",
  "لینا": "lena",
  "لیتا": "leta",
  "لیتی": "leti",
  "لیتے": "lete",
  "لیا": "liya",
  "لے": "le",
  "لو": "lo",
  "لیں": "lain",
  "لینے": "lene",
  "کہنا": "kehna",
  "کہا": "kaha",
  "کہتا": "kehta",
  "کہتی": "kehti",
  "کہتے": "kehte",
  "کہو": "kaho",
  "کہنے": "kehne",
  "دیکھنا": "dekhna",
  "دیکھا": "dekha",
  "دیکھو": "dekho",
  "دیکھتا": "dekhta",
  "دیکھتی": "dekhti",
  "دیکھتے": "dekhte",
  "دیکھنے": "dekhne",
  "سننا": "sunna",
  "سنا": "suna",
  "سنو": "suno",
  "سنتا": "sunta",
  "سنتی": "sunti",
  "سنتے": "sunte",
  "سننے": "sunne",
  "بولنا": "bolna",
  "بولا": "bola",
  "بولو": "bolo",
  "بولتا": "bolta",
  "پڑھنا": "parhna",
  "پڑھا": "parha",
  "پڑھو": "parho",
  "پڑھتا": "parhta",
  "پڑھتی": "parhti",
  "پڑھتے": "parhte",
  "پڑھنے": "parhne",
  "لکھنا": "likhna",
  "لکھا": "likha",
  "لکھو": "likho",
  "لکھتا": "likhta",
  "لکھتی": "likhti",
  "لکھتے": "likhte",
  "لکھنے": "likhne",
  "سمجھنا": "samajhna",
  "سمجھا": "samjha",
  "سمجھ": "samajh",
  "سمجھو": "samjho",
  "سمجھنے": "samajhne",
  "سمجھانا": "samjhana",
  "سمجھایا": "samjhaya",
  "جاننا": "janna",
  "جانتا": "janta",
  "جانتی": "janti",
  "جانتے": "jante",
  "معلوم": "maloom",
  "چاہنا": "chahna",
  "چاہتا": "chahta",
  "چاہتی": "chahti",
  "چاہتے": "chahte",
  "چاہیے": "chahiye",
  "سکنا": "sakna",
  "سکتا": "sakta",
  "سکتی": "sakti",
  "سکتے": "sakte",
  "رہنا": "rehna",
  "رہا": "raha",
  "رہی": "rahi",
  "رہے": "rahe",
  "رہتا": "rehta",
  "رہتی": "rehti",
  "رہتے": "rehte",
  "رہنے": "rehne",
  "بننا": "banna",
  "بنانا": "banana",
  "بنایا": "banaya",
  "بنائی": "banayi",
  "بنائے": "banaye",
  "بنا": "bana",
  "ملنا": "milna",
  "ملا": "mila",
  "ملتا": "milta",
  "ملتی": "milti",
  "ملتے": "milte",
  "ملی": "mili",
  "ملے": "mile",
  "ملانا": "milana",
  "کھانا": "khana",
  "کھایا": "khaya",
  "کھاؤ": "khao",
  "کھاتا": "khata",
  "کھاتی": "khati",
  "پینا": "peena",
  "پیا": "piya",
  "پیو": "peeyo",
  "پیتا": "peeta",
  "سونا": "sona",
  "سویا": "soya",
  "سوتے": "sote",
  "اٹھنا": "uthna",
  "اٹھا": "utha",
  "اٹھو": "utho",
  "اٹھانا": "uthana",
  "اٹھایا": "uthaya",
  "بیٹھنا": "baithna",
  "بیٹھا": "baitha",
  "بیٹھو": "baitho",
  "چلنا": "chalna",
  "چلا": "chala",
  "چلو": "chalo",
  "چلتا": "chalta",
  "چلتی": "chalti",
  "چلتے": "chalte",
  "چلانا": "chalana",
  "چلایا": "chalaya",
  "دوڑنا": "daurna",
  "دوڑا": "daura",
  "بھاگنا": "bhagna",
  "بھاگا": "bhaga",
  "لانا": "lana",
  "لایا": "laya",
  "لائے": "laye",
  "لائی": "layi",
  "لاؤ": "lao",
  "رکھنا": "rakhna",
  "رکھا": "rakha",
  "رکھو": "rakho",
  "رکھتے": "rakhte",
  "مانگنا": "mangna",
  "مانگا": "manga",
  "پوچھنا": "poochna",
  "پوچھا": "poocha",
  "پوچھو": "poocho",
  "بتانا": "batana",
  "بتایا": "bataya",
  "بتاؤ": "batao",
  "کھولنا": "kholna",
  "کھولا": "khola",
  "کھلا": "khula",
  "مرنا": "marna",
  "مرا": "mara",
  "مرگیا": "margaya",
  "جینا": "jeena",
  "جیا": "jiya",
  "مارنا": "maarna",
  "مارا": "maara",
  "مارو": "maaro",
  "بچانا": "bachana",
  "بچایا": "bachaya",
  "بچنا": "bachna",
  "بچا": "bacha",
  "جلانا": "jalana",
  "جلایا": "jalaya",
  "جلنا": "jalna",
  "جلا": "jala",
  "بجھانا": "bujhana",
  "بجھنا": "bujhna",
  "ناچنا": "nachna",
  "گانا": "gana",
  "گایا": "gaya",
  "لڑنا": "larna",
  "لڑا": "lara",
  "جیتنا": "jeetna",
  "جیتا": "jeeta",
  "ہارنا": "haarna",
  "ہارا": "haara",
  "چھپانا": "chhupana",
  "چھپنا": "chhupna",
  "ڈھونڈنا": "dhoondna",
  "ڈھونڈا": "dhoonda",
  "بدلنا": "badalna",
  "بدلا": "badla",
  "رکنا": "rukna",
  "روکنا": "rokna",
  "روکا": "roka",
  "اڑنا": "urna",
  "اڑانا": "urana",
  "تیرنا": "tairna",
  "کھیلنا": "khelna",
  "کھیلا": "khela",
  "کھیلتا": "khelta",
  "ہنسنا": "hansna",
  "ہنسا": "hansa",
  "رونا": "rona",
  "رویا": "roya",
  "ڈرنا": "darna",
  "ڈرا": "dara",
  "ڈرانا": "darana",
  "بھولنا": "bhoolna",
  "بھولا": "bhoola",
  "یاد": "yaad",
  "سیکھنا": "seekhna",
  "سیکھا": "seekha",
  "سکھانا": "sikhana",
  "سکھایا": "sikhaya",
  "پکانا": "pakana",
  "پکایا": "pakaya",
  "پکنا": "pakna",
  "پکا": "paka",
  "دھونا": "dhona",
  "دھویا": "dhoya",
  "پہننا": "pehanna",
  "پہنا": "pehna",
  "اتارنا": "utarna",
  "اتارا": "utara",
  "اترو": "utro",
  "اترنا": "utarna",
  "بیچنا": "bechna",
  "بیچا": "becha",
  "خریدنا": "khareedna",
  "خریدا": "khareeda",
  "بھیجنا": "bhejna",
  "بھیجا": "bheja",
  "پہنچنا": "pohanchna",
  "پہنچا": "pohancha",
  "پہنچانا": "pohonchana",
  "نکلنا": "nikalna",
  "نکلا": "nikla",
  "نکالنا": "nikalna",
  "نکالا": "nikala",
  "گرنا": "girna",
  "گرا": "gira",
  "گرانا": "girana",
  "پکڑنا": "pakarna",
  "پکڑا": "pakra",
  "چھوڑنا": "chhorna",
  "چھوڑا": "chhora",
  "توڑنا": "torna",
  "توڑا": "tora",
  "ٹوٹنا": "tootna",
  "جوڑنا": "jorna",
  "جوڑا": "jora",
  "کاٹنا": "katna",
  "کاٹا": "kata",
  "ڈالنا": "daalna",
  "ڈالا": "dala",
  "ماننا": "manna",
  "مانا": "mana",
  "پہچاننا": "pehchanna",
  "پہچانا": "pehchana",
  "بلانا": "bulana",
  "بلایا": "bulaya",
  "پکارنا": "pukarna",
  "دکھانا": "dikhana",
  "دکھایا": "dikhaya",
  "سنانا": "sunana",
  "سنایا": "sunaya",
  "پھینکنا": "phenkna",
  "پھینکا": "phenka",
  "کھینچنا": "kheinchna",
  "کھینچا": "kheincha",
  "لیٹنا": "letna",
  "جاگنا": "jagna",
  "جگانا": "jagana",
  "کھلانا": "khilana",
  "پلانا": "pilana",
  "پلٹنا": "palatna",
  "لوٹنا": "lautna",
  "ٹوکنا": "tokna",
  "گزارنا": "guzarna",
  "گزرنا": "guzarna",
  "گزر": "guzar",
  "بیتنا": "beetna",
  "جھگڑنا": "jhagarna",
  "سہنا": "sehna",
  "کمانا": "kamana",
  "کمایا": "kamaya",
  "چھونا": "chhoona",
  "چکھنا": "chakhna",
  "سونگھنا": "soonghna",
  "گننا": "ginna",
  "تولنا": "tolna",
  "ناپنا": "naapna",
  "باندھنا": "bandhna",
  "باندھا": "bandha",
  "بھرنا": "bharna",
  "بھرا": "bhara",
  "ڈوبنا": "doobna",
  "ڈوبا": "dooba",
  "چمکنا": "chamakna",
  "چمکا": "chamka",
  "مہکنا": "mehakna",
  "گھومنا": "ghoomna",
  "پھرنا": "phirna",
  "چھیننا": "chheenna",
  "پھیلنا": "phailna",
  "پھیلانا": "phailana",
  "سمٹنا": "simatna",
  "والا": "wala",
  "والی": "wali",
  "والے": "walay",
  "شروع": "shuru",
  "ختم": "khatam",
  "بند": "band",
  "پیدا": "paida",

  // ---- people and family ----
  "آدمی": "aadmi",
  "عورت": "aurat",
  "لڑکا": "larka",
  "لڑکی": "larki",
  "لڑکے": "larke",
  "لڑکیاں": "larkiyan",
  "بچہ": "bacha",
  "بچی": "bachi",
  "بچے": "bache",
  "بچوں": "bachon",
  "ماں": "maan",
  "باپ": "baap",
  "والد": "walid",
  "والدہ": "walida",
  "والدین": "walidain",
  "امی": "ammi",
  "ابو": "abbu",
  "بھائی": "bhai",
  "بہن": "behan",
  "باجی": "baji",
  "آپی": "api",
  "بیٹا": "beta",
  "بیٹی": "beti",
  "بیٹے": "bete",
  "بیٹیاں": "betiyan",
  "پوتا": "pota",
  "پوتی": "poti",
  "نواسا": "nawasa",
  "نواسی": "nawasi",
  "دادا": "dada",
  "دادی": "dadi",
  "نانا": "nana",
  "نانی": "nani",
  "چچا": "chacha",
  "چچی": "chachi",
  "ماموں": "mamoon",
  "ممانی": "mami",
  "خالہ": "khala",
  "خالو": "khalu",
  "پھوپھی": "phupi",
  "پھوپھا": "phupha",
  "شوہر": "shauhar",
  "بیوی": "biwi",
  "دولہا": "dulha",
  "دلہن": "dulhan",
  "شادی": "shaadi",
  "نکاح": "nikah",
  "رشتہ": "rishta",
  "خاندان": "khandan",
  "مہمان": "mehman",
  "میزبان": "mezban",
  "دوست": "dost",
  "دشمن": "dushman",
  "پڑوسی": "parosi",
  "ہمسایہ": "humsaya",
  "ساتھی": "sathi",
  "بزرگ": "buzurg",
  "جوان": "jawan",
  "نوجوان": "naujawan",
  "بوڑھا": "boorha",
  "بوڑھی": "boorhi",
  "انسان": "insan",
  "لوگ": "log",
  "عوام": "awam",
  "شخص": "shakhs",
  "فرد": "fard",

  // ---- professions & roles ----
  "استاد": "ustad",
  "استانی": "ustani",
  "طالب": "talib",
  "شاگرد": "shagird",
  "ڈاکٹر": "doctor",
  "حکیم": "hakeem",
  "طبیب": "tabeeb",
  "نرس": "nurse",
  "سرجن": "surgeon",
  "وکیل": "wakeel",
  "جج": "judge",
  "منصف": "munsif",
  "کسان": "kisan",
  "زمیندار": "zameendar",
  "مزدور": "mazdoor",
  "کاریگر": "karigar",
  "درزی": "darzi",
  "موچی": "mochi",
  "دھوبی": "dhobi",
  "حجام": "hajam",
  "نائی": "nai",
  "باورچی": "bawarchi",
  "خانساماں": "khansama",
  "چوکیدار": "chowkidar",
  "گارڈ": "guard",
  "پولیس": "police",
  "سپاہی": "sipahi",
  "فوجی": "fauji",
  "افسر": "afsar",
  "مینیجر": "manager",
  "ڈائریکٹر": "director",
  "صدر": "sadar",
  "وزیر": "wazeer",
  "سفیر": "safeer",
  "تاجر": "tajir",
  "دکاندار": "dukandar",
  "ڈرائیور": "driver",
  "پائلٹ": "pilot",
  "انجینئر": "engineer",
  "سائنسدان": "sciencedan",
  "شاعر": "shair",
  "ادیب": "adeeb",
  "مصنف": "musannif",
  "صحافی": "sahafi",
  "فنکار": "fankar",
  "گلوکار": "gulokar",
  "اداکار": "adakar",
  "کھلاڑی": "khilari",

  // ---- places & buildings ----
  "گھر": "ghar",
  "مکان": "makan",
  "کمرہ": "kamra",
  "دروازہ": "darwaza",
  "کھڑکی": "khirki",
  "چھت": "chhat",
  "دیوار": "deewar",
  "فرش": "farsh",
  "صحن": "sehan",
  "خانہ": "khana",
  "مدرسہ": "madrasa",
  "سکول": "school",
  "کالج": "college",
  "یونیورسٹی": "university",
  "مکتب": "maktab",
  "جامعہ": "jamia",
  "لائبریری": "library",
  "ہسپتال": "hospital",
  "شفاخانہ": "shifakhana",
  "کلینک": "clinic",
  "دواخانہ": "dawakhana",
  "دکان": "dukan",
  "مارکیٹ": "market",
  "بازار": "bazaar",
  "مال": "mall",
  "پلازہ": "plaza",
  "ہوٹل": "hotel",
  "ریستوران": "restaurant",
  "ڈھابہ": "dhaba",
  "مسجد": "masjid",
  "مندر": "mandir",
  "گرجا": "girja",
  "مزار": "mazar",
  "درگاہ": "dargah",
  "قبرستان": "qabristan",
  "شہر": "shehar",
  "قصبہ": "qasba",
  "گاؤں": "gaon",
  "دیہات": "dehat",
  "ملک": "mulk",
  "وطن": "watan",
  "دنیا": "dunya",
  "جہاں": "jahan",
  "راستہ": "rasta",
  "سڑک": "sarak",
  "گلی": "gali",
  "کوچہ": "koocha",
  "محلہ": "mohalla",
  "چوک": "chowk",
  "شاہراہ": "shahrah",
  "پل": "pul",
  "باغ": "bagh",
  "پارک": "park",
  "میدان": "maidan",
  "اسٹیڈیم": "stadium",
  "دفتر": "daftar",
  "تھانہ": "thana",
  "چوکی": "chowki",
  "جیل": "jail",
  "قیدخانہ": "qaidkhana",
  "عدالت": "adalat",
  "کچہری": "kachehri",
  "اسٹیشن": "station",
  "ایئرپورٹ": "airport",
  "بندرگاہ": "bandargah",
  "کارخانہ": "karkhana",
  "فیکٹری": "factory",

  // ---- English loanwords in everyday Urdu ----
  "کمپیوٹر": "computer",
  "موبائل": "mobile",
  "فون": "phone",
  "انٹرنیٹ": "internet",
  "بس": "bus",
  "ٹرین": "train",
  "کار": "car",
  "سائیکل": "cycle",
  "موٹر": "motor",
  "ٹریفک": "traffic",
  "سگنل": "signal",
  "پیٹرول": "petrol",
  "ڈیزل": "diesel",
  "گیس": "gas",
  "بیٹری": "battery",
  "چارجر": "charger",
  "کیمرہ": "camera",
  "ریڈیو": "radio",
  "ٹیلیویژن": "television",
  "ٹیلیگرام": "telegram",
  "ویڈیو": "video",
  "آڈیو": "audio",
  "مائیک": "mic",
  "سپیکر": "speaker",
  "سکرین": "screen",
  "سافٹویئر": "software",
  "ہارڈویئر": "hardware",
  "ویب": "web",
  "ایپ": "app",
  "ایپلیکیشن": "application",
  "ایمیل": "email",
  "میسج": "message",
  "چیٹ": "chat",
  "آنلائن": "online",
  "آفلائن": "offline",
  "اکاؤنٹ": "account",
  "پاسورڈ": "password",
  "سیکیورٹی": "security",
  "سرور": "server",
  "نیٹورک": "network",
  "فائل": "file",
  "فولڈر": "folder",
  "لنک": "link",
  "پوسٹ": "post",
  "کمنٹ": "comment",
  "چینل": "channel",
  "گروپ": "group",
  "ڈاؤنلوڈ": "download",
  "اپلوڈ": "upload",
  "سرچ": "search",
  "کلک": "click",
  "ٹائپ": "type",
  "ٹیسٹ": "test",
  "نمبر": "number",
  "کلاس": "class",
  "ڈگری": "degree",
  "سسٹم": "system",
  "پروگرام": "program",
  "ٹیکنالوجی": "technology",
  "ڈیٹا": "data",
  "ٹکٹ": "ticket",
  "پاسپورٹ": "passport",
  "ویزا": "visa",
  "بینک": "bank",
  "چیک": "cheque",
  "کارڈ": "card",
  "ٹیکس": "tax",
  "بل": "bill",
  "وارڈ": "ward",
  "آپریشن": "operation",
  "ایمبولینس": "ambulance",

  // ---- time, calendar, days, months, weather ----
  "دن": "din",
  "رات": "raat",
  "صبح": "subah",
  "شام": "shaam",
  "سحر": "sehar",
  "دوپہر": "dopahar",
  "پہر": "pehar",
  "وقت": "waqt",
  "گھنٹہ": "ghanta",
  "منٹ": "minute",
  "سیکنڈ": "second",
  "لمحہ": "lamha",
  "ہفتہ": "hafta",
  "مہینہ": "maheena",
  "سال": "saal",
  "صدی": "sadi",
  "زمانہ": "zamana",
  "مدت": "muddat",
  "عمر": "umar",
  "آج": "aaj",
  "کل": "kal",
  "پرسوں": "parson",
  "ترسوں": "tarson",
  "ابھی": "abhi",
  "اب": "ab",
  "پھر": "phir",
  "تب": "tab",
  "جب": "jab",
  "ہمیشہ": "hamesha",
  "کبھی": "kabhi",
  "اکثر": "aksar",
  "روز": "roz",
  "روزانہ": "rozana",
  "ماہانہ": "mahana",
  "سالانہ": "salana",
  "پیر": "peer",
  "منگل": "mangal",
  "بدھ": "budh",
  "جمعرات": "jumeraat",
  "جمعہ": "jumma",
  "اتوار": "itwar",
  "جنوری": "january",
  "فروری": "february",
  "مارچ": "march",
  "اپریل": "april",
  "مئی": "may",
  "جون": "june",
  "جولائی": "july",
  "اگست": "august",
  "ستمبر": "september",
  "اکتوبر": "october",
  "نومبر": "november",
  "دسمبر": "december",
  "موسم": "mausam",
  "گرمی": "garmi",
  "سردی": "sardi",
  "بہار": "bahaar",
  "خزاں": "khazan",
  "برسات": "barsaat",
  "بارش": "barish",
  "دھوپ": "dhoop",
  "چھاؤں": "chhaon",
  "بادل": "badal",
  "برف": "baraf",
  "اولے": "olay",
  "کہر": "kohar",
  "دھند": "dhund",
  "آندھی": "aandhi",
  "طوفان": "toofan",
  "سیلاب": "sailab",
  "زلزلہ": "zalzala",
  "آسمان": "asman",
  "زمین": "zameen",
  "سورج": "sooraj",
  "چاند": "chand",
  "ستارہ": "sitara",
  "کہکشان": "kahkashan",

  // ---- household objects, clothing, tools ----
  "کتاب": "kitaab",
  "قلم": "qalam",
  "کاغذ": "kaghaz",
  "دوات": "dawat",
  "سیاہی": "siyahi",
  "پنسل": "pencil",
  "بستہ": "basta",
  "بیگ": "bag",
  "میز": "mez",
  "کرسی": "kursi",
  "الماری": "almari",
  "صوفہ": "sofa",
  "پلنگ": "palang",
  "بستر": "bistar",
  "گدا": "gadda",
  "چادر": "chadar",
  "تکیہ": "takiya",
  "کمبل": "kambal",
  "لحاف": "lihaf",
  "پردہ": "parda",
  "قالین": "qaleen",
  "چٹائی": "chatai",
  "پنکھا": "pankha",
  "چولہا": "choolha",
  "فریج": "fridge",
  "برتن": "bartan",
  "چمچ": "chamach",
  "کانٹا": "kanta",
  "چھری": "chhuri",
  "چاقو": "chaqoo",
  "پلیٹ": "plate",
  "پیالی": "pyali",
  "پیالہ": "pyala",
  "کپ": "cup",
  "گلاس": "glass",
  "جگ": "jug",
  "بوتل": "botal",
  "ڈبہ": "dabba",
  "بالٹی": "balti",
  "مگ": "mug",
  "جھاڑو": "jharoo",
  "صابن": "sabun",
  "تولیہ": "toliya",
  "آئینہ": "aaina",
  "شیشہ": "sheesha",
  "کنگھی": "kanghi",
  "سوئی": "sui",
  "دھاگہ": "dhaga",
  "قینچی": "qainchi",
  "تالا": "tala",
  "چابی": "chabi",
  "گھڑی": "ghari",
  "ٹوپی": "topi",
  "پگڑی": "pagri",
  "قمیض": "qameez",
  "شلوار": "shalwar",
  "پاجامہ": "pajama",
  "پینٹ": "pant",
  "شرٹ": "shirt",
  "کوٹ": "coat",
  "واسکوٹ": "waistcoat",
  "شال": "shawl",
  "دوپٹہ": "dupatta",
  "جراب": "jurab",
  "دستانے": "dastaney",
  "جوتا": "joota",
  "چپل": "chappal",
  "صندل": "sandal",
  "کپڑا": "kapra",
  "ریشم": "resham",
  "سوت": "soot",
  "اون": "oon",

  // ---- food, spices, ingredients, kitchen ----
  "پانی": "paani",
  "دودھ": "doodh",
  "دہی": "dahi",
  "مکھن": "makhan",
  "گھی": "ghee",
  "پنیر": "paneer",
  "ملائی": "malai",
  "چائے": "chai",
  "قہوہ": "qehwa",
  "کافی": "coffee",
  "شربت": "sharbat",
  "لسی": "lassi",
  "جوس": "juice",
  "روٹی": "roti",
  "نان": "naan",
  "پراٹھا": "paratha",
  "کلچہ": "kulcha",
  "چاول": "chawal",
  "بریانی": "biryani",
  "پلاؤ": "pulao",
  "سالن": "salan",
  "شوربہ": "shorba",
  "قورمہ": "qorma",
  "کڑاہی": "karahi",
  "نہاری": "nihari",
  "حلیم": "haleem",
  "کباب": "kabab",
  "تکہ": "tikka",
  "سموسہ": "samosa",
  "پکوڑا": "pakaura",
  "رول": "roll",
  "دال": "daal",
  "گوشت": "gosht",
  "قیمہ": "qeema",
  "مرغی": "murghi",
  "مچھلی": "machhli",
  "انڈا": "anda",
  "سبزی": "sabzi",
  "آٹا": "aata",
  "میدہ": "maida",
  "سوجی": "sooji",
  "بیسن": "besan",
  "تیل": "tel",
  "نمک": "namak",
  "چینی": "cheeni",
  "شکر": "shukr",
  "گڑ": "gur",
  "شہد": "shehad",
  "مرچ": "mirch",
  "ہلدی": "haldi",
  "دھنیا": "dhaniya",
  "زیرہ": "zeera",
  "لہسن": "lehsun",
  "ادرک": "adrak",
  "پیاز": "pyaz",
  "ٹماٹر": "tamatar",
  "آلو": "aloo",
  "گاجر": "gajar",
  "مولی": "mooli",
  "شلجم": "shaljam",
  "مٹر": "matar",
  "پالک": "palak",
  "میتھی": "methi",
  "گوبھی": "gobhi",
  "بینگن": "baingan",
  "بھنڈی": "bhindi",
  "کریلا": "karela",
  "کدو": "kaddu",
  "لوکی": "lauki",
  "ٹینڈا": "tinda",
  "کھیرا": "kheera",
  "پھل": "phal",
  "آم": "aam",
  "سیب": "saib",
  "کیلا": "kela",
  "انگور": "angoor",
  "انار": "anar",
  "امرود": "amrood",
  "مالٹا": "malta",
  "کینو": "kinnu",
  "لیمو": "leemo",
  "لیموں": "leemo",
  "تربوز": "tarbooz",
  "خربوزہ": "kharbooza",
  "آڑو": "aadoo",
  "خوبانی": "khubani",
  "ناشپاتی": "nashpati",
  "چیری": "cherry",
  "پپیتا": "papeeta",
  "لیچی": "lychee",
  "بادام": "badam",
  "پستہ": "pista",
  "اخروٹ": "akhrot",
  "کاجو": "kaju",
  "مونگفلی": "moongphali",
  "کشمش": "kishmish",
  "کھجور": "khajoor",
  "کھیر": "kheer",
  "حلوہ": "halwa",
  "زردہ": "zarda",
  "جامن": "jamun",
  "جلیبی": "jalebi",
  "برفی": "barfi",
  "رسگلہ": "rasgulla",
  "کلفی": "kulfi",
  "آئسکریم": "icecream",
  "کیک": "cake",
  "بسکٹ": "biscuit",
  "چاکلیٹ": "chocolate",
  "مٹھائی": "mithai",
  "سلاد": "salad",
  "رائتہ": "raita",
  "چٹنی": "chatni",
  "اچار": "achaar",
  "مربہ": "murabba",

  // ---- anatomy & health ----
  "دل": "dil",
  "دماغ": "dimagh",
  "جسم": "jism",
  "بدن": "badan",
  "جان": "jaan",
  "روح": "rooh",
  "سر": "sar",
  "کھوپڑی": "khopri",
  "پیشانی": "peshani",
  "ماتھا": "matha",
  "آنکھ": "aankh",
  "آنکھیں": "aankhein",
  "پلک": "palak",
  "ابرو": "abroo",
  "کان": "kaan",
  "ناک": "naak",
  "منہ": "munh",
  "ہونٹ": "hont",
  "دانت": "daant",
  "زبان": "zuban",
  "گلا": "gala",
  "گردن": "gardan",
  "کندھا": "kandha",
  "بازو": "bazu",
  "کہنی": "kohni",
  "کلائی": "kalai",
  "ہاتھ": "hath",
  "انگلی": "ungli",
  "انگلیاں": "ungliyan",
  "انگوٹھا": "angootha",
  "ناخن": "nakhun",
  "سینہ": "seena",
  "چھاتی": "chhati",
  "پیٹ": "pet",
  "کمر": "kamar",
  "پشت": "pusht",
  "ران": "raan",
  "گھٹنا": "ghutna",
  "ٹانگ": "taang",
  "ٹانگیں": "taangein",
  "پاؤں": "paon",
  "ایڑی": "eeri",
  "تالو": "talu",
  "ہڈی": "haddi",
  "خون": "khoon",
  "رگ": "rag",
  "پٹھا": "pattha",
  "جلد": "jild",
  "چمڑا": "chamra",
  "بال": "baal",
  "داڑھی": "darhi",
  "مونچھ": "moochh",
  "چہرہ": "chehra",
  "رخسار": "rukhsar",
  "گال": "gaal",
  "ٹھوڑی": "thori",
  "جگر": "jigar",
  "گردہ": "gurda",
  "پھیپھڑا": "phepra",
  "معدہ": "maida",
  "صحت": "sehat",
  "تندرستی": "tandurusti",
  "بیماری": "beemari",
  "مرض": "marz",
  "مریض": "mareez",
  "بخار": "bukhar",
  "درد": "dard",
  "تکلیف": "takleef",
  "کھانسی": "khansi",
  "نزلہ": "nazla",
  "زکام": "zukam",
  "الرجی": "allergy",
  "انفیکشن": "infection",
  "وائرس": "virus",
  "زخم": "zakhm",
  "چوٹ": "chot",
  "علاج": "ilaj",
  "دوا": "dawa",
  "ادویات": "adwiyat",
  "شفا": "shifa",
  "گولی": "goli",
  "ٹیکہ": "teeka",
  "مرہم": "marham",
  "پٹی": "patti",

  // ---- feelings, morals, abstraction ----
  "محبت": "mohabbat",
  "عشق": "ishq",
  "پیار": "pyaar",
  "الفت": "ulfat",
  "چاہت": "chahat",
  "نفرت": "nafrat",
  "حسد": "hasad",
  "کینہ": "keena",
  "بغض": "bughz",
  "خوشی": "khushi",
  "مسرت": "musarrat",
  "غم": "gham",
  "دکھ": "dukh",
  "رنج": "ranj",
  "الم": "alam",
  "افسوس": "afsos",
  "پچھتاوا": "pachtawa",
  "ندامت": "nadamat",
  "ڈر": "dar",
  "خوف": "khauf",
  "دہشت": "dehshat",
  "امید": "umeed",
  "آس": "aas",
  "یاس": "yaas",
  "مایوسی": "mayoosi",
  "یقین": "yaqeen",
  "اعتماد": "aitamad",
  "بھروسہ": "bharosa",
  "شک": "shak",
  "سچ": "sach",
  "جھوٹ": "jhoot",
  "انصاف": "insaf",
  "عدل": "adl",
  "ظلم": "zulm",
  "ستم": "sitam",
  "عزت": "izzat",
  "احترام": "ehtiram",
  "بےعزتی": "beizzati",
  "ذلت": "zillat",
  "رسوائی": "ruswai",
  "شکریہ": "shukriya",
  "صبر": "sabr",
  "احسان": "ehsan",
  "معاف": "maaf",
  "معافی": "maafi",
  "دعا": "dua",
  "بددعا": "baddua",
  "خواب": "khwab",
  "خیال": "khayal",
  "ارادہ": "irada",
  "خواہش": "khwahish",
  "تمنا": "tamanna",
  "آرزو": "aarzoo",
  "حسرت": "hasrat",
  "زندگی": "zindagi",
  "حیات": "hayat",
  "موت": "maut",
  "وفات": "wafat",
  "طاقت": "taqat",
  "قوت": "quwwat",
  "زور": "zor",
  "ہمت": "himmat",
  "حوصلہ": "hosla",
  "جذبہ": "jazba",
  "شوق": "shauq",
  "کمزوری": "kamzori",
  "ناتوانی": "natawani",
  "ضرورت": "zaroorat",
  "حاجت": "hajat",
  "مدد": "madad",
  "تعاون": "taawun",
  "کوشش": "koshish",
  "جدوجہد": "jaddojahad",
  "محنت": "mehnat",
  "مشقت": "mushaqqat",
  "کامیابی": "kamyabi",
  "فتح": "fateh",
  "ناکامی": "nakami",
  "شکست": "shikast",
  "ہار": "haar",
  "جیت": "jeet",
  "فیصلہ": "faisla",
  "رائے": "raye",
  "مشورہ": "mashwara",
  "غصہ": "ghussa",
  "طیش": "taish",
  "غرور": "ghuroor",
  "تکبر": "takabbur",
  "عاجزی": "aajzi",
  "انکساری": "inkisari",
  "سادگی": "sadgi",
  "صفائی": "safai",
  "پاکی": "paki",
  "شرم": "sharm",
  "حیا": "haya",
  "غیرت": "ghairat",
  "حمیت": "hameeyat",
  "اخلاق": "akhlaq",
  "شرافت": "sharafat",
  "دیانت": "diyanat",
  "امانت": "amanat",
  "خیانت": "khiyanat",
  "وفا": "wafa",
  "بےوفائی": "bewafai",
  "دوستی": "dosti",
  "دشمنی": "dushmani",
  "حیرت": "hairat",
  "تعجب": "taajjub",
  "راحت": "rahat",
  "سکون": "sakoon",
  "اطمینان": "itminan",
  "بےچینی": "bechaini",
  "اضطراب": "iztirab",
  "احساس": "ehsas",
  "شعور": "shaoor",
  "لاپرواہی": "laparwahi",
  "حقیقت": "haqeeqat",
  "راز": "raaz",
  "بھید": "bhed",

  // ---- knowledge, education, language, arts ----
  "علم": "ilm",
  "تعلیم": "taleem",
  "تربیت": "tarbiyat",
  "سبق": "sabaq",
  "درس": "dars",
  "امتحان": "imtihan",
  "آزمائش": "azmaish",
  "نتیجہ": "nateeja",
  "مسئلہ": "masla",
  "حل": "hal",
  "سوال": "sawal",
  "جواب": "jawab",
  "بحث": "behas",
  "مباحثہ": "mubahisa",
  "بات": "baat",
  "باتیں": "baatein",
  "گفتگو": "guftagu",
  "کلام": "kalam",
  "لفظ": "lafz",
  "الفاظ": "alfaz",
  "جملہ": "jumla",
  "فقرہ": "fiqra",
  "حرف": "harf",
  "حروف": "haroof",
  "معنی": "maani",
  "مطلب": "matlab",
  "نام": "naam",
  "پتہ": "pata",
  "خبر": "khabar",
  "اخبار": "akhbar",
  "رسالہ": "risala",
  "مجلہ": "majalla",
  "کہانی": "kahani",
  "قصہ": "qissa",
  "داستان": "dastan",
  "ناول": "novel",
  "افسانہ": "afsana",
  "مضمون": "mazmoon",
  "مقالہ": "maqala",
  "شعر": "sher",
  "اشعار": "ashaar",
  "غزل": "ghazal",
  "نظم": "nazm",
  "مرثیہ": "marsiya",
  "قصیدہ": "qaseeda",
  "ادب": "adab",
  "تاریخ": "tareekh",
  "جغرافیہ": "jughrafia",
  "ریاضی": "riyazi",
  "سائنس": "science",
  "فلسفہ": "falsafa",
  "منطق": "mantiq",
  "ثقافت": "saqafat",
  "تہذیب": "tehzeeb",
  "تمدن": "tamaddun",
  "رسم": "rasm",
  "رواج": "riwaj",
  "موسیقی": "moseeqi",
  "راگ": "raag",
  "ساز": "saaz",
  "تصویر": "tasweer",
  "نقشہ": "naqsha",
  "رنگ": "rang",
  "آواز": "awaz",
  "شور": "shor",
  "خاموشی": "khamoshi",
  "سکوت": "sakoot",
  "تحقیق": "tehqeeq",
  "تلاش": "talash",
  "کھوج": "khoj",

  // ---- religion & spiritual ----
  "نماز": "namaz",
  "صلوہ": "salat",
  "روزہ": "roza",
  "صوم": "saum",
  "زکوہ": "zakat",
  "حج": "hajj",
  "عمرہ": "umrah",
  "عید": "eid",
  "قربانی": "qurbani",
  "مذہب": "mazhab",
  "اسلام": "islam",
  "اللہ": "allah",
  "رب": "rabb",
  "خدا": "khuda",
  "رسول": "rasool",
  "نبی": "nabi",
  "پیغمبر": "paighambar",
  "صحابی": "sahabi",
  "صحابہ": "sahaba",
  "اہل": "ahl",
  "بیت": "bait",
  "قرآن": "quran",
  "حدیث": "hadees",
  "سنت": "sunnat",
  "شریعت": "shariat",
  "فقہ": "fiqh",
  "دین": "deen",
  "ایمان": "imaan",
  "عقیدہ": "aqeeda",
  "جنت": "jannat",
  "فردوس": "firdaus",
  "جہنم": "jahannum",
  "دوزخ": "dozakh",
  "فرشتہ": "farishta",
  "ملائکہ": "malaika",
  "شیطان": "shaitan",
  "ابلیس": "iblees",
  "جنات": "jinnat",
  "قیامت": "qayamat",
  "حشر": "hashr",
  "حساب": "hisaab",
  "میزان": "meezan",
  "صراط": "sirat",
  "عبادت": "ibadat",
  "بندگی": "bandagi",
  "سجدہ": "sajda",
  "رکوع": "rukoo",
  "تلاوت": "tilawat",
  "تسبیح": "tasbeeh",
  "ذکر": "zikr",
  "توبہ": "tauba",
  "استغفار": "istighfar",
  "ثواب": "sawab",
  "اجر": "ajar",
  "گناہ": "gunah",
  "معصیت": "masiyat",
  "عذاب": "azaab",
  "رحمت": "rehmat",
  "برکت": "barkat",
  "فضل": "fazal",
  "کرم": "karam",
  "نیکی": "naiki",
  "بدی": "badi",
  "تقوی": "taqwa",
  "پرہیزگاری": "parhezgari",
  "ولایت": "wilayat",
  "ولی": "wali",
  "صوفی": "sufi",
  "درویش": "darwesh",
  "فقیر": "faqeer",

  // ---- news, politics, governance, law ----
  "سیاست": "siyasat",
  "سیاسی": "siyasi",
  "الیکشن": "election",
  "انتخاب": "intikhab",
  "انتخابات": "intikhabat",
  "ووٹ": "vote",
  "امیدوار": "ummedwar",
  "پارٹی": "party",
  "جماعت": "jamaat",
  "اسمبلی": "assembly",
  "پارلیمنٹ": "parliament",
  "ایوان": "aiwan",
  "سینیٹ": "senate",
  "قانون": "qanoon",
  "دستور": "dastoor",
  "آئین": "aaeen",
  "جمہوریت": "jamhooriat",
  "آمریت": "aamriyat",
  "حکومت": "hukumat",
  "ریاست": "riyasat",
  "سلطنت": "saltanat",
  "حکمران": "hukmaran",
  "اپوزیشن": "opposition",
  "حزب": "hizb",
  "اختلاف": "ikhtilaf",
  "اتحاد": "ittehad",
  "تنظیم": "tanzeem",
  "ادارہ": "idara",
  "کمپنی": "company",
  "جرم": "jurm",
  "مجرم": "mujrim",
  "ملزم": "mulzim",
  "قیدی": "qaidi",
  "گواہ": "gawah",
  "شہادت": "shahadat",
  "ثبوت": "saboot",
  "سزا": "saza",
  "جرمانہ": "jurmana",
  "فوج": "fauj",
  "لشکر": "lashkar",
  "عساکر": "asakir",
  "جنگ": "jang",
  "معرکہ": "maraka",
  "لڑائی": "larai",
  "امن": "aman",
  "صلح": "sulah",
  "معاہدہ": "muahida",
  "پیمان": "paiman",
  "معیشت": "maeeshat",
  "اقتصادیات": "iqtisadiyat",
  "تجارت": "tijarat",
  "کاروبار": "karobar",
  "صنعت": "sanat",
  "حرفت": "hirfat",
  "بجٹ": "budget",
  "خسارہ": "khasara",
  "منافع": "munafa",
  "نقصان": "nuqsan",
  "ترقی": "taraqqi",
  "تنزل": "tanazzul",
  "منصوبہ": "mansooba",
  "پروجیکٹ": "project",
  "اعلان": "elaan",
  "اشتہار": "ishtihar",
  "رپورٹ": "report",
  "بیان": "bayan",
  "اجلاس": "ijlas",
  "کانفرنس": "conference",
  "دورہ": "dora",
  "ملاقات": "mulaqat",
  "مذاکرات": "muzakrat",
  "تقریب": "taqreeb",
  "جلسہ": "jalsa",
  "جلوس": "juloos",
  "ہڑتال": "hartal",
  "مظاہرہ": "muzahira",
  "احتجاج": "ehtejaj",
  "تقریر": "taqreer",
  "خطاب": "khitab",
  "پیغام": "paigham",

  // ---- nature, environment, animals ----
  "درخت": "darakht",
  "شجر": "shajar",
  "پودا": "pauda",
  "پودے": "pauday",
  "پتے": "pattay",
  "پھول": "phool",
  "کلی": "kali",
  "شاخ": "shaakh",
  "ٹہنی": "tahni",
  "جڑ": "jar",
  "تنا": "tana",
  "گھاس": "ghaas",
  "سبزہ": "sabza",
  "جنگل": "jungle",
  "بیابان": "bayaban",
  "صحرا": "sehra",
  "ریگستان": "registan",
  "پہاڑ": "pahar",
  "کوہ": "koh",
  "چوٹی": "choti",
  "وادی": "wadi",
  "درہ": "darra",
  "غار": "ghaar",
  "دریا": "darya",
  "نہر": "nehar",
  "ندی": "nadi",
  "نالہ": "nala",
  "سمندر": "samandar",
  "بحر": "behar",
  "ساحل": "sahil",
  "کنارہ": "kinara",
  "جزیرہ": "jazeera",
  "جھیل": "jheel",
  "تالاب": "talab",
  "حوض": "hauz",
  "کنواں": "kuan",
  "چشمہ": "chashma",
  "آبشار": "aabshar",
  "مٹی": "mitti",
  "ریت": "rait",
  "پتھر": "patthar",
  "چٹان": "chatan",
  "چاندی": "chandi",
  "تانبا": "tanba",
  "لوہا": "loha",
  "پیتل": "peetal",
  "کوئلہ": "koyla",
  "آگ": "aag",
  "شعلہ": "shola",
  "دھواں": "dhuwan",
  "راکھ": "raakh",
  "روشنی": "roshni",
  "نور": "noor",
  "اندھیرا": "andhera",
  "تاریکی": "tareeki",
  "سایہ": "saya",
  "کتا": "kutta",
  "کتیا": "kutiya",
  "بلی": "billi",
  "بلا": "billa",
  "گھوڑا": "ghora",
  "گھوڑی": "ghori",
  "گدھا": "gadha",
  "گدھی": "gadhi",
  "خچر": "khachhar",
  "گائے": "gaye",
  "بیل": "bail",
  "بچھڑا": "bachhra",
  "بھینس": "bhains",
  "بکری": "bakri",
  "بکرا": "bakra",
  "بھیڑ": "bheed",
  "دنبہ": "dumbah",
  "شیر": "sheer",
  "ببر": "babar",
  "چیتا": "cheeta",
  "تیندوا": "tendwa",
  "بھیڑیا": "bhediya",
  "لومڑی": "lomri",
  "ریچھ": "reech",
  "ہاتھی": "hathi",
  "ہتھنی": "hathni",
  "اونٹ": "oont",
  "سانڈنی": "sandni",
  "ہرن": "hiran",
  "سنگھا": "singha",
  "خرگوش": "khargosh",
  "بندر": "bandar",
  "چوہا": "chooha",
  "چوہیا": "chuhiya",
  "پرندہ": "parinda",
  "چرند": "charand",
  "پرند": "parand",
  "کوا": "kawa",
  "طوطا": "tota",
  "مینا": "maina",
  "چڑیا": "chiriya",
  "کبوتر": "kabootar",
  "فاختہ": "fakhta",
  "عقاب": "uqaab",
  "باز": "baaz",
  "شاہین": "shaheen",
  "چیل": "cheel",
  "گدھ": "gidh",
  "الو": "ulloo",
  "بگلا": "bagla",
  "بطخ": "battakh",
  "ہنس": "hans",
  "مور": "mor",
  "مرغ": "murgh",
  "مرغا": "murgha",
  "مرغابی": "murghabi",
  "کوئل": "koyal",
  "بلبل": "bulbul",
  "سانپ": "saanp",
  "ناگ": "naag",
  "چھپکلی": "chhipkali",
  "مگرمچھ": "magarmachh",
  "کچھوا": "kachhwa",
  "مینڈک": "maindak",
  "مچھر": "machhar",
  "مکھی": "makkhi",
  "مکڑی": "makri",
  "تتلی": "titli",
  "کیڑا": "keera",
  "کیڑے": "keeray",
  "پتنگا": "patanga",
  "چیونٹی": "cheenti",
  "بچھو": "bichhoo",

  // ---- adjectives ----
  "اچھا": "acha",
  "اچھی": "achi",
  "اچھے": "achay",
  "برا": "bura",
  "بری": "buri",
  "برے": "buray",
  "بڑا": "bara",
  "بڑی": "bari",
  "بڑے": "baray",
  "چھوٹا": "chhota",
  "چھوٹی": "chhoti",
  "چھوٹے": "chhotay",
  "نیا": "naya",
  "نئی": "nayi",
  "نئے": "naye",
  "پرانا": "purana",
  "پرانی": "purani",
  "پرانے": "puranay",
  "لمبا": "lamba",
  "لمبی": "lambi",
  "لمبے": "lambay",
  "موٹا": "mota",
  "موٹی": "moti",
  "موٹے": "motay",
  "پتلا": "patla",
  "پتلی": "patli",
  "پتلے": "patlay",
  "دبلا": "dubla",
  "تیز": "tez",
  "سست": "sust",
  "آہستہ": "ahista",
  "گرم": "garam",
  "ٹھنڈا": "thanda",
  "ٹھنڈی": "thandi",
  "ٹھنڈے": "thanday",
  "سرد": "sard",
  "خشک": "khushk",
  "گیلا": "geela",
  "گیلی": "geeli",
  "تر": "tarr",
  "میٹھا": "meetha",
  "میٹھی": "meethi",
  "میٹھے": "meethay",
  "کڑوا": "karwa",
  "کڑوی": "karwi",
  "کھٹا": "khatta",
  "کھٹی": "khatti",
  "نمکین": "namkeen",
  "پھیکا": "pheeka",
  "صاف": "saaf",
  "ستھرا": "suthra",
  "گندا": "ganda",
  "گندی": "gandi",
  "خالی": "khali",
  "بھری": "bhari",
  "بھرے": "bharay",
  "مشکل": "mushkil",
  "دشوار": "dushwar",
  "آسان": "aasan",
  "سہل": "sehal",
  "ضروری": "zaroori",
  "لازمی": "lazmi",
  "مکمل": "mukammal",
  "ادھورا": "adhoora",
  "تیار": "tayyar",
  "خوش": "khush",
  "ناراض": "naraz",
  "خفا": "khafa",
  "پریشان": "pareshan",
  "اداس": "udaas",
  "غمگین": "ghamgeen",
  "تھکا": "thaka",
  "تھکی": "thaki",
  "تھکے": "thakay",
  "بیمار": "beemar",
  "تندرست": "tandurust",
  "امیر": "ameer",
  "غریب": "ghareeb",
  "نادار": "nadar",
  "مفلس": "muflis",
  "مشہور": "mashhoor",
  "معروف": "maroof",
  "گمنام": "gumnam",
  "خوبصورت": "khoobsurat",
  "بدصورت": "badsurat",
  "قبیح": "qabeeh",
  "ذہین": "zaheen",
  "عقلمند": "aqlmand",
  "دانا": "dana",
  "ہوشیار": "hoshiyar",
  "چالاک": "chalak",
  "بےوقوف": "bewaqoof",
  "احمق": "ahmaq",
  "نادان": "nadan",
  "بہادر": "bahadur",
  "شجاع": "shuja",
  "دلیر": "daler",
  "بزدل": "buzdil",
  "ڈرپوک": "darpok",
  "محنتی": "mehnati",
  "سچا": "sacha",
  "سچی": "sachi",
  "سچے": "sachay",
  "جھوٹا": "jhoota",
  "جھوٹی": "jhooti",
  "جھوٹے": "jhootay",
  "ایماندار": "imandar",
  "دیانتدار": "diyanatdar",
  "بےایمان": "beiman",
  "پیارا": "pyara",
  "پیاری": "pyari",
  "پیارے": "pyaray",
  "عجیب": "ajeeb",
  "انوکھا": "anokha",
  "نرالا": "nirala",
  "خاص": "khaas",
  "مخصوص": "makhsoos",
  "عام": "aam",
  "سستا": "sasta",
  "سستی": "sasti",
  "مہنگا": "mehanga",
  "مہنگی": "mehangi",
  "بھاری": "bhari",
  "وزنی": "wazni",
  "ہلکا": "halka",
  "ہلکی": "halki",
  "سخت": "sakht",
  "نرم": "naram",
  "ملائم": "mulaim",
  "روشن": "roshan",
  "تاریک": "tareek",
  "گہرا": "gehra",
  "گہری": "gehri",
  "اونچا": "ooncha",
  "اونچی": "oonchi",
  "پست": "past",
  "چوڑا": "chaura",
  "چوڑی": "chauri",
  "تنگ": "tang",
  "مضبوط": "mazboot",
  "پائیدار": "paayedar",
  "کمزور": "kamzor",
  "نازک": "nazuk",

  // ---- colours ----
  "سفید": "safaid",
  "کالا": "kala",
  "کالی": "kali",
  "کالے": "kalay",
  "سیاہ": "siyah",
  "سرخ": "surkh",
  "لال": "laal",
  "نیلا": "neela",
  "نیلی": "neeli",
  "نیلے": "neelay",
  "ہرا": "hara",
  "ہری": "hari",
  "ہرے": "haray",
  "سبز": "sabz",
  "پیلا": "peela",
  "پیلی": "peeli",
  "پیلے": "peelay",
  "زرد": "zard",
  "بھورا": "bhura",
  "بھوری": "bhuri",
  "گلابی": "gulabi",
  "نارنجی": "naranji",
  "جامنی": "jamni",
  "بیگنی": "baingani",
  "سرمئی": "surmai",
  "سنہری": "sunehri",
  "روپہلی": "rupahli",

  // ---- numbers & ordinals ----
  "صفر": "sifar",
  "تین": "teen",
  "چار": "char",
  "پانچ": "panch",
  "چھ": "chhe",
  "سات": "saat",
  "آٹھ": "aath",
  "نو": "nau",
  "دس": "das",
  "گیارہ": "gyarah",
  "بارہ": "barah",
  "تیرہ": "terah",
  "چودہ": "chaudah",
  "پندرہ": "pandrah",
  "سولہ": "solah",
  "سترہ": "satrah",
  "اٹھارہ": "atharah",
  "انیس": "unnees",
  "بیس": "bees",
  "اکیس": "ikkees",
  "بائیس": "baees",
  "تیئیس": "taees",
  "چوبیس": "chaubees",
  "پچیس": "pachees",
  "چھبیس": "chhabees",
  "ستائیس": "sataees",
  "اٹائیس": "athaees",
  "انتیس": "untees",
  "تیس": "tees",
  "چالیس": "chalees",
  "پچاس": "pachas",
  "ساٹھ": "saath",
  "ستر": "sattar",
  "اسی": "assi",
  "نوے": "nabbay",
  "سو": "so",
  "ہزار": "hazar",
  "لاکھ": "lakh",
  "کروڑ": "karor",
  "ارب": "arab",
  "پہلا": "pehla",
  "پہلی": "pehli",
  "دوسرا": "dosra",
  "دوسری": "dosri",
  "دوسرے": "dosray",
  "تیسرا": "teesra",
  "تیسری": "teesri",
  "تیسرے": "teesray",
  "چوتھا": "chautha",
  "چوتھی": "chauthi",
  "چوتھے": "chauthay",
  "پانچواں": "panchwan",
  "پانچویں": "panchwein",
  "چھٹا": "chhatta",
  "چھٹی": "chhatti",
  "ساتواں": "saatwan",
  "ساتویں": "saatwein",
  "آٹھواں": "aathwan",
  "آٹھویں": "aathwein",
  "نواں": "nawwan",
  "نویں": "nawein",
  "دسواں": "daswan",
  "دسویں": "daswein",
  "آخری": "aakhri",
  "اول": "awwal",
  "دوم": "dom",
  "سوم": "som",

  // ---- greetings, discourse markers, interjections ----
  "سلام": "salam",
  "خداحافظ": "khudahafiz",
  "حافظ": "hafiz",
  "مہربانی": "meherbani",
  "مبارک": "mubarak",
  "مبارکباد": "mubarakbaad",
  "خوشامد": "khushamad",
  "خوشامدید": "khushamdeed",
  "اہلاً": "ahlan",
  "وسہلاً": "wasahlan",
  "ہاں": "haan",
  "جی": "ji",
  "ٹھیک": "theek",
  "درست": "durust",
  "صحیح": "sahi",
  "غلط": "ghalat",
  "بالکل": "bilkul",
  "ضرور": "zaroor",
  "شاید": "shayad",
  "کاش": "kaash",
  "واہ": "waah",
  "شاباش": "shabash",
  "آفرین": "aafreen",
  "مرحبا": "marhaba",
  "سبحان": "subhan",
  "الحمد": "alhamd",
  "ماشاء": "masha",
  "انشاء": "insha",
  "جلدی": "jaldi",
  "شتابی": "shitabi",
  "دیر": "der",
  "تاخیر": "takheer",
  "آرام": "aaram",
  "تھوڑا": "thora",
  "تھوڑی": "thori",
  "تھوڑے": "thoray",
  "زیادہ": "ziyada",
  "بہت": "bohat",
  "کم": "kam",
  "صرف": "sirf",
  "محض": "mahez",
  "فقط": "faqat",
  "واقعی": "waqai",
  "مچ": "mach",

  // ---- cities, regions, countries ----
  "پاکستان": "pakistan",
  "کراچی": "karachi",
  "لاہور": "lahore",
  "آباد": "abad",
  "راولپنڈی": "rawalpindi",
  "پشاور": "peshawar",
  "کوئٹہ": "quetta",
  "ملتان": "multan",
  "فیصل": "faisal",
  "سیالکوٹ": "sialkot",
  "گوجرانوالہ": "gujranwala",
  "حیدرآباد": "hyderabad",
  "بہاولپور": "bahawalpur",
  "سرگودھا": "sargodha",
  "سکھر": "sukkur",
  "لاڑکانہ": "larkana",
  "شیخوپورہ": "sheikhupura",
  "جھنگ": "jhang",
  "گجرات": "gujrat",
  "مردان": "mardan",
  "قصور": "kasur",
  "صوابی": "swabi",
  "ایبٹ": "abbott",
  "گلگت": "gilgit",
  "بلتستان": "baltistan",
  "مظفرآباد": "muzaffarabad",
  "گوادر": "gwadar",
  "سکردو": "skardu",
  "سوات": "swat",
  "چترال": "chitral",
  "پنجاب": "punjab",
  "سندھ": "sindh",
  "بلوچستان": "balochistan",
  "خیبر": "khyber",
  "پختونخوا": "pakhtunkhwa",
  "کشمیر": "kashmir",
  "بھارت": "bharat",
  "ہندوستان": "hindustan",
  "چین": "cheen",
  "افغانستان": "afghanistan",
  "ایران": "iran",
  "سعودی": "saudi",
  "عرب": "arab",
  "امارات": "imarat",
  "دبئی": "dubai",
  "قطر": "qatar",
  "کویت": "kuwait",
  "بحرین": "bahrain",
  "عمان": "oman",
  "عراق": "iraq",
  "فلسطین": "palestine",
  "اردن": "urdan",
  "مصر": "misr",
  "ترکی": "turkey",
  "برطانیہ": "bartania",
  "لندن": "london",
  "امریکہ": "amreeka",
  "واشنگٹن": "washington",
  "نیویارک": "newyork",
  "کینیڈا": "canada",
  "جرمنی": "germany",
  "فرانس": "france",
  "پیرس": "paris",
  "اٹلی": "italy",
  "روس": "roos",
  "ماسکو": "moscow",
  "جاپان": "japan",
  "ٹوکیو": "tokyo",
  "آسٹریلیا": "australia",
  "بیجنگ": "beijing",
  "دہلی": "delhi",
  "قاہرہ": "cairo",
  "مکہ": "makkah",
  "مدینہ": "madina",
  "مقدس": "muqaddas",

  // ---- names that appear constantly in Urdu content ----
  "محمد": "muhammad",
  "احمد": "ahmed",
  "علی": "ali",
  "خان": "khan",
  "زید": "zaid",
  "حسن": "hasan",
  "حسین": "hussain",
  "عثمان": "usman",
  "ابوبکر": "abubakar",
  "بلال": "bilal",
  "اسلم": "aslam",
  "حمزہ": "hamza",
  "سعد": "saad",
  "خالد": "khalid",
  "طارق": "tariq",
  "ولید": "waleed",
  "شاہ": "shah",
  "چوہدری": "chaudhary",
  "شیخ": "sheikh",
  "سید": "syed",
  "قریشی": "qureshi",
  "بٹ": "butt",
  "مغل": "mughal",
  "انصاری": "ansari",
  "فاروق": "farooq",
  "سلمان": "salman",
  "عمران": "imran",
  "نوید": "naveed",
  "کامران": "kamran",
  "ارسلان": "arslan",
  "عرفان": "irfan",
  "رضوان": "rizwan",
  "عدنان": "adnan",
  "فیضان": "faizan",
  "شہزاد": "shehzad",
  "وقار": "waqar",
  "بابر": "babar",
  "رضا": "raza",
  "طاہر": "tahir",
  "ناصر": "nasir",
  "زبیر": "zubair",
  "سہیل": "sohail",
  "ندیم": "nadeem",
  "وسیم": "waseem",
  "نعیم": "naeem",
  "سلیم": "saleem",
  "جمیل": "jameel",
  "راشد": "rashid",
  "ساجد": "sajid",
  "ماجد": "majid",
  "عابد": "abid",
  "زاہد": "zahid",
  "شاہد": "shahid",
  "زین": "zain",
  "حارث": "haris",
  "ریان": "rayyan",
  "قاسم": "qasim",
  "طیب": "tayyab",
  "ابراہیم": "ibrahim",
  "اسماعیل": "ismail",
  "اسحاق": "ishaq",
  "یعقوب": "yaqoob",
  "یوسف": "yousuf",
  "موسی": "musa",
  "عیسی": "eesa",
  "داؤد": "dawood",
  "سلیمان": "sulaiman",
  "یونس": "younus",
  "ایوب": "ayub",
  "ہارون": "haroon",
  "یحیی": "yahya",
  "فاطمہ": "fatima",
  "عائشہ": "ayesha",
  "مریم": "maryam",
  "زینب": "zainab",
  "خدیجہ": "khadija",
  "حفصہ": "hafsa",
  "سارہ": "sara",
  "ہدی": "huda",
  "ثنا": "sana",
  "حنا": "hina",
  "اقرا": "iqra",
  "سدرہ": "sidra",
  "اقصی": "aqsa",
  "فرح": "farah",
  "ندا": "nida",
  "صبا": "saba",
  "مہوش": "mehwish",
  "انعم": "anam",
  "ارم": "iram",
  "بشری": "bushra",
  "سمیرا": "sumaira",
  "نازیہ": "nazia",
  "شازیہ": "shazia",
  "روبینہ": "rubina",
  "نسرین": "nasreen",
  "یاسمین": "yasmeen",
  "ثمینہ": "samina",
  "پروین": "parveen",
  "شبانہ": "shabana",
  "فرزانہ": "farzana",
  "طاہرہ": "tahira",
  "عصمت": "ismat",
  "نصرت": "nusrat",

  // ---- classical, poetic and literary vocabulary ----
  "گلستاں": "gulistan",
  "گلشن": "gulshan",
  "بہاراں": "baharan",
  "پروانہ": "parwana",
  "شمع": "shama",
  "ساقی": "saqi",
  "میخانہ": "maikhana",
  "جام": "jaam",
  "صراحی": "surahi",
  "رند": "rind",
  "محتسب": "muhtasib",
  "قاصد": "qasid",
  "پیامبر": "payambar",
  "رقیب": "raqeeb",
  "حبیب": "habeeb",
  "محبوب": "mahboob",
  "معشوق": "mashooq",
  "صنم": "sanam",
  "سنگدل": "sangdil",
  "بیدرد": "bedard",
  "غمگسار": "ghamgusar",
  "مسیحا": "maseeha",
  "ناصح": "nasih",
  "واعظ": "waiz",
  "تشنہ": "tishna",
  "گیسو": "gesu",
  "زلف": "zulf",
  "دیدار": "deedar",
  "جلوہ": "jalwa",
  "حجاب": "hijab",
  "نقاب": "niqab",
  "سوز": "soz",
  "آہنگ": "aahang",
  "نغمہ": "naghma",
  "ترانہ": "tarana",
  "داستاں": "dastan",
  "حور": "hoor",
  "پری": "pari",
  "دیو": "deo",
  "جادو": "jadoo",
  "طلسم": "tilism",
  "افسون": "afsoon",
  "سحرانگیز": "sehrangeez",
  "فسوں": "fusoon",
  "جنوں": "junoon",
  "سودا": "sauda",
  "وحشت": "wahshat",
  "ارمان": "arman",
  "ذوق": "zauq",
  "وجد": "wajd",
  "مستی": "masti",
  "کیف": "kaif",
  "خمار": "khumar",
  "نشہ": "nasha",
  "بےخودی": "bekhudi",

  // ---- commerce, finance and modern technology ----
  "خریداری": "khareedari",
  "فروخت": "farokht",
  "سرمایہ": "sarmaya",
  "قرضہ": "qarza",
  "بینکاری": "bainkari",
  "قسط": "qist",
  "ادائیگی": "adaigi",
  "آمدنی": "aamdani",
  "خرچہ": "kharcha",
  "بچت": "bachat",
  "محصول": "mahsool",
  "مالیت": "maliyat",
  "کتابت": "kitabat",
  "منڈی": "mandi",
  "نیلامی": "neelami",
  "بولی": "boli",
  "شراکت": "sharakat",
  "حصص": "hisas",
  "اسٹاک": "stock",
  "دولت": "daulat",
  "ثروت": "sarwat",
  "غربت": "ghurbat",
  "افلاس": "iflas",
  "پروفائل": "profile",
  "سیٹنگز": "settings",
  "نوٹیفکیشن": "notification",
  "سائٹ": "site",
  "پورٹل": "portal",

  // ---- administration, law, governance and politics ----
  "عدلیہ": "adliya",
  "مقننہ": "muqannana",
  "انتظامیہ": "intizamia",
  "حکمرانی": "hukmrani",
  "جمہوریہ": "jamhooria",
  "صدارت": "sadarat",
  "وزارت": "wazarat",
  "سفارت": "safarat",
  "قونصل": "consul",
  "سفارتخانہ": "safaratkhana",
  "آئینی": "aaeeni",
  "دستوری": "dastoori",
  "فوجداری": "faujdari",
  "دیوانی": "deewani",
  "مقدمہ": "muqaddama",
  "سماعت": "samaat",
  "درخواست": "darkhwast",
  "اپیل": "appeal",
  "ضمانت": "zamanat",
  "بریت": "bariyat",
  "عمرقید": "umrqaid",
  "پھانسی": "phansi",
  "وکالت": "wukalat",
  "حقوق": "huqooq",
  "فرائض": "faraiz",
  "شہری": "shahri",
  "شہریت": "shahriyat",
  "شناخت": "shanakht",
  "نادرا": "nadra",
  "مجلس": "majlis",
  "شوری": "shoora",
  "حلیف": "haleef",
  "مخالف": "mukhalif",
  "بغاوت": "baghawat",

  // ---- geography, nature and terrain ----
  "خطہ": "khitta",
  "صوبہ": "sooba",
  "ضلع": "zila",
  "تحصیل": "tehsil",
  "یونین": "union",
  "کونسل": "council",
  "قریہ": "qarya",
  "بستی": "basti",
  "شہرستان": "shehristan",
  "نخلستان": "nakhalistan",
  "کوہسار": "kohsar",
  "کہسار": "kuhsar",
  "گھاٹی": "ghati",
  "وادیاں": "wadiyan",
  "کھائی": "khaai",
  "خلیج": "khaleej",
  "راس": "raas",

  // ---- verbs and verbal inflections ----
  "مانتے": "mante",
  "مانتی": "manti",
  "مانو": "mano",
  "ٹھہرنا": "theharna",
  "ٹھہرا": "thehra",
  "ٹھہرو": "thehro",
  "ٹھہرتے": "thehrte",
  "سمجھاؤ": "samjhao",
  "پہنچاؤ": "pohonchao",
  "لٹانا": "lutana",
  "لٹایا": "lutaya",
  "لوٹا": "loota",
  "پکارا": "pukara",
  "پکارو": "pukaro",
  "جھکنا": "jhukna",
  "جھکا": "jhuka",
  "جھکاؤ": "jhukao",
  "جھکانا": "jhukana",
  "تھامنا": "thamna",
  "تھاما": "thama",
  "تھامو": "thamo",
  "سنوارنا": "sanwarna",
  "سنوارا": "sanwara",
  "نکھارنا": "nikharna",
  "نکھارا": "nikhara",
  "سنبھالنا": "sambhalna",
  "سنبھالا": "sambhala",
  "سنبھالو": "sambhalo",
  "پچھتانا": "pachtana",
  "پچھتایا": "pachtaya",
  "گھبرانا": "ghabrana",
  "گھبرایا": "ghabraya",
  "مسکرانا": "muskurana",
  "مسکرایا": "muskuraya",
  "مسکراہٹ": "muskurahat",
  "جھنجھلانا": "jhanjhlana",
  "تلملانا": "tilmilana",
  "بلبلانا": "bilbilana",
  "کھلکھلانا": "khilkhilana",
  "کھٹکھٹانا": "khatkhatana",
  "جھلملانا": "jhilmilana",
  "چمچماتا": "chamchamata",
  "لہرانا": "lehrana",
  "لہرایا": "lehraya",
  "لہر": "lehar",
};

/**
 * Extra Roman spellings that map back to Urdu, for the reverse direction only.
 *
 * Roman Urdu has no standard orthography, so the same word arrives spelled many
 * ways. These are the variants common enough to be worth hard-coding, plus
 * disambiguations where two Urdu words share one Roman form.
 */
export const ROMAN_VARIANTS: Record<string, string> = {
  hay: "ہے",
  he: "ہے",
  hei: "ہے",
  hein: "ہیں",
  hn: "ہیں",
  hain: "ہیں",
  hun: "ہوں",
  hoon: "ہوں",
  ap: "آپ",
  aap: "آپ",
  aapko: "آپکو",
  aapka: "آپکا",
  aapki: "آپکی",
  aapke: "آپکے",
  kaise: "کیسے",
  kese: "کیسے",
  kaisey: "کیسے",
  kaisa: "کیسا",
  kaisi: "کیسی",
  nam: "نام",
  naam: "نام",
  mai: "میں",
  main: "میں",
  me: "میں",
  mein: "میں",
  mujhe: "مجھے",
  mujhey: "مجھے",
  mjhe: "مجھے",
  mjhy: "مجھے",
  mj: "مجھے",
  humein: "ہمیں",
  hume: "ہمیں",
  hmein: "ہمیں",
  hmain: "ہمیں",
  tumhein: "تمہیں",
  tumhe: "تمہیں",
  tumhey: "تمہیں",
  tmhein: "تمہیں",
  tmhe: "تمہیں",
  unhein: "انہیں",
  unhe: "انہیں",
  unhey: "انہیں",
  inhein: "انہیں",
  inhe: "انہیں",
  usey: "اسے",
  usay: "اسے",
  use: "اسے",
  isay: "اسے",
  ise: "اسے",
  jisay: "جسے",
  jise: "جسے",
  kisay: "کسے",
  kise: "کسے",
  kyu: "کیوں",
  kyn: "کیوں",
  kyo: "کیوں",
  kyun: "کیوں",
  kyoon: "کیوں",
  kaha: "کہاں",
  kahan: "کہاں",
  khn: "کہاں",
  kidhar: "کدھر",
  idhar: "ادھر",
  jidhar: "جدھر",
  kab: "کب",
  kya: "کیا",
  kia: "کیا",
  kiya: "کیا",
  kuch: "کچھ",
  kuchh: "کچھ",
  kch: "کچھ",
  bhi: "بھی",
  b: "بھی",
  nahin: "نہیں",
  nahi: "نہیں",
  nai: "نہیں",
  nhi: "نہیں",
  nahe: "نہیں",
  nhn: "نہیں",
  bahut: "بہت",
  bhot: "بہت",
  bohot: "بہت",
  bohat: "بہت",
  bht: "بہت",
  achha: "اچھا",
  acha: "اچھا",
  accha: "اچھا",
  achi: "اچھی",
  achhi: "اچھی",
  ache: "اچھے",
  achay: "اچھے",
  shukria: "شکریہ",
  shukriya: "شکریہ",
  thx: "شکریہ",
  thanks: "شکریہ",
  plz: "مہربانی",
  please: "مہربانی",
  salaam: "سلام",
  salam: "سلام",
  mohabat: "محبت",
  muhabbat: "محبت",
  mohabbat: "محبت",
  pyar: "پیار",
  pyaar: "پیار",
  zindagi: "زندگی",
  zindgi: "زندگی",
  dost: "دوست",
  dst: "دوست",
  khush: "خوش",
  khushi: "خوش",
  theak: "ٹھیک",
  thik: "ٹھیک",
  theek: "ٹھیک",
  bhai: "بھائی",
  bhaiya: "بھائی",
  behen: "بہن",
  behan: "بہن",
  baji: "باجی",
  api: "آپی",
  ammi: "امی",
  ami: "امی",
  abbu: "ابو",
  abu: "ابو",
  paisay: "پیسے",
  paise: "پیسے",
  paisa: "پیسہ",
  kaam: "کام",
  kam: "کم",
  ghar: "گھر",
  ghr: "گھر",
  pehle: "پہلے",
  pehlay: "پہلے",
  pehly: "پہلے",
  baad: "بعد",
  bad: "بعد",
  lekin: "لیکن",
  lkn: "لیکن",
  magar: "مگر",
  mgr: "مگر",
  waqt: "وقت",
  wqt: "وقت",
  zroor: "ضرور",
  zaroor: "ضرور",
  zarur: "ضرور",
  zroori: "ضروری",
  zaroori: "ضروری",
  chahiye: "چاہیے",
  chahye: "چاہیے",
  chaheye: "چاہیے",
  khuda: "خدا",
  allah: "اللہ",
  hafiz: "حافظ",
  haafiz: "حافظ",
  khudahafiz: "خداحافظ",
  // aam is both آم (mango) and عام (common); the fruit is the likelier query.
  aam: "آم",
  hawa: "ہوا",
  // sona is both سونا (to sleep) and سونا (gold) — same spelling, no conflict.
  ho: "ہو",
  hoga: "ہوگا",
  hogi: "ہوگی",
  honge: "ہوںگے",
  karna: "کرنا",
  krna: "کرنا",
  karo: "کرو",
  kro: "کرو",
  dekhna: "دیکھنا",
  dekho: "دیکھو",
  sunna: "سننا",
  suno: "سنو",
  samajh: "سمجھ",
  smjh: "سمجھ",
  laikin: "لیکن",
  shukr: "شکر",
  sabr: "صبر",
  dil: "دل",
  kitab: "کتاب",
  kitaab: "کتاب",
  school: "سکول",
  college: "کالج",
  hospital: "ہسپتال",
  pal: "پل",
  daur: "دور",
  kurta: "کرتا",
  haseen: "حسین",
  malik: "ملک",
  kidhr: "کدھر",
  idhr: "ادھر",
  jdhr: "جدھر",
  thek: "ٹھیک",
  thk: "ٹھیک",
  sahi: "صحیح",
  galat: "غلط",
  ghalat: "غلط",
  sb: "سب",
  hmsha: "ہمیشہ",
  kbhi: "کبھی",
  aksar: "اکثر",
  behtareen: "بہترین",
  zabardast: "زبردست",
  shandar: "شاندار",
  khoob: "خوب",
  khub: "خوب",
  sharam: "شرم",
  haya: "حیا",
  ghairat: "غیرت",
  himmat: "ہمت",
  hosla: "حوصلہ",
  mashaallah: "ماشاءاللہ",
  inshaallah: "انشاءاللہ",
  mashallah: "ماشاءاللہ",
  inshallah: "انشاءاللہ",
  alhamdulillah: "الحمدللہ",
  jazakallah: "جزاک‌اللہ",
  // ---- additional Roman variants for common words ----
  // verbs
  hona: "ہونا",
  hua: "ہوا",
  hui: "ہوئی",
  hue: "ہوئے",
  jana: "جانا",
  jata: "جاتا",
  jati: "جاتی",
  gaya: "گیا",
  gayi: "گئی",
  aana: "آنا",
  aati: "آتی",
  aaya: "آیا",
  aayi: "آئی",
  dena: "دینا",
  deta: "دیتا",
  deti: "دیتی",
  diya: "دیا",
  lena: "لینا",
  leta: "لیتا",
  leti: "لیتی",
  liya: "لیا",
  kehna: "کہنا",
  kehta: "کہتا",
  kehti: "کہتی",
  dekhta: "دیکھتا",
  dekhti: "دیکھتی",
  dekha: "دیکھا",
  bolna: "بولنا",
  bolta: "بولتا",
  boli: "بولی",
  bola: "بولا",
  padhna: "پڑھنا",
  padhta: "پڑھتا",
  padhti: "پڑھتی",
  padha: "پڑھا",
  likhna: "لکھنا",
  likhta: "لکھتا",
  likhti: "لکھتی",
  likha: "لکھا",
  samajhna: "سمجھنا",
  samjha: "سمجھا",
  chahna: "چاہنا",
  chahta: "چاہتا",
  chahti: "چاہتی",
  sakna: "سکنا",
  sakta: "سکتا",
  sakti: "سکتی",
  rehna: "رہنا",
  rehta: "رہتا",
  rehti: "رہتی",
  raha: "رہا",
  rahi: "رہی",
  banna: "بننا",
  bana: "بنا",
  banana: "بنانا",
  banaya: "بنایا",
  milna: "ملنا",
  milta: "ملتا",
  milti: "ملتی",
  mila: "ملا",
  mili: "ملی",
  khata: "کھاتا",
  khati: "کھاتی",
  khaya: "کھایا",
  peena: "پینا",
  peeta: "پیتا",
  piya: "پیا",
  sota: "سوتا",
  soti: "سوتی",
  soya: "سویا",
  uthna: "اٹھنا",
  utha: "اٹھا",
  baithna: "بیٹھنا",
  baitha: "بیٹھا",
  chalta: "چلتا",
  chalti: "چلتی",
  chala: "چلا",
  rakhna: "رکھنا",
  rakha: "رکھا",
  maangna: "مانگنا",
  manga: "مانگا",
  puchna: "پوچھنا",
  poocha: "پوچھا",
  batana: "بتانا",
  bataya: "بتایا",
  kholna: "کھولنا",
  khola: "کھولا",
  marna: "مرنا",
  mara: "مرا",
  jeetna: "جیتنا",
  jeeta: "جیتا",
  haarna: "ہارنا",
  haara: "ہارا",
  badalna: "بدلنا",
  badla: "بدلا",
  rukna: "رکنا",
  rokna: "روکنا",
  roka: "روکا",
  khelna: "کھیلنا",
  khela: "کھیلا",
  hansna: "ہنسنا",
  hansa: "ہنسا",
  rona: "رونا",
  roya: "رویا",
  darna: "ڈرنا",
  dara: "ڈرا",
  bhoolna: "بھولنا",
  bhoola: "بھولا",
  seekhna: "سیکھنا",
  seekha: "سیکھا",
  sikhana: "سکھانا",
  sikhaya: "سکھایا",
  dhona: "دھونا",
  dhoya: "دھویا",
  pehanna: "پہننا",
  pehna: "پہنا",
  bechna: "بیچنا",
  becha: "بیچا",
  khareedna: "خریدنا",
  khareeda: "خریدا",
  bhejna: "بھیجنا",
  bheja: "بھیجا",
  nikalna: "نکلنا",
  nikla: "نکلا",
  girna: "گرنا",
  gira: "گرا",
  pakarna: "پکڑنا",
  pakra: "پکڑا",
  chhorna: "چھوڑنا",
  chhora: "چھوڑا",
  torna: "توڑنا",
  tora: "توڑا",
  katna: "کاٹنا",
  kata: "کاٹا",
  daalna: "ڈالنا",
  dala: "ڈالا",
  manna: "ماننا",
  mana: "مانا",
  bulana: "بلانا",
  bulaya: "بلایا",
  dikhana: "دکھانا",
  dikhaya: "دکھایا",
  phenkna: "پھینکنا",
  phenka: "پھینکا",
  letna: "لیٹنا",
  jagna: "جاگنا",
  lautna: "لوٹنا",
  guzarna: "گزارنا",
  kamana: "کمانا",
  kamaya: "کمایا",
  ginna: "گننا",
  bandhna: "باندھنا",
  bandha: "باندھا",
  bharna: "بھرنا",
  bhara: "بھرا",
  doobna: "ڈوبنا",
  chamakna: "چمکنا",
  ghoomna: "گھومنا",
  phirna: "پھرنا",
  phailna: "پھیلنا",
  // nouns
  insaan: "انسان",
  larka: "لڑکا",
  larki: "لڑکی",
  bacha: "بچہ",
  bachi: "بچی",
  maan: "ماں",
  baap: "باپ",
  beta: "بیٹا",
  beti: "بیٹی",
  dushman: "دشمن",
  makaan: "مکان",
  kamra: "کمرہ",
  darwaza: "دروازہ",
  khirki: "کھڑکی",
  chhat: "چھت",
  deewar: "دیوار",
  farsh: "فرش",
  qalam: "قلم",
  kaghaz: "کاغذ",
  mez: "میز",
  kursi: "کرسی",
  almari: "الماری",
  palang: "پلنگ",
  bistar: "بستر",
  parda: "پردہ",
  qaleen: "قالین",
  bartan: "برتن",
  chamach: "چمچ",
  plate: "پلیٹ",
  pyali: "پیالی",
  cup: "کپ",
  glass: "گلاس",
  botal: "بوتل",
  dabba: "ڈبہ",
  jharoo: "جھاڑو",
  sabun: "صابن",
  aaina: "آئینہ",
  sheesha: "شیشہ",
  kanghi: "کنگھی",
  sui: "سوئی",
  qainchi: "قینچی",
  tala: "تالا",
  chabi: "چابی",
  ghari: "گھڑی",
  topi: "ٹوپی",
  pagri: "پگڑی",
  qameez: "قمیض",
  shalwar: "شلوار",
  pajama: "پاجامہ",
  pant: "پینٹ",
  shirt: "شرٹ",
  coat: "کوٹ",
  shawl: "شال",
  dupatta: "دوپٹہ",
  joota: "جوتا",
  chappal: "چپل",
  sandal: "صندل",
  kapra: "کپڑا",
  doodh: "دودھ",
  dahi: "دہی",
  ghee: "گھی",
  chai: "چائے",
  qehwa: "قہوہ",
  coffee: "کافی",
  lassi: "لسی",
  juice: "جوس",
  roti: "روٹی",
  naan: "نان",
  paratha: "پراٹھا",
  chawal: "چاول",
  biryani: "بریانی",
  pulao: "پلاؤ",
  salan: "سالن",
  qorma: "قورمہ",
  nihari: "نہاری",
  haleem: "حلیم",
  kabab: "کباب",
  tikka: "تکہ",
  samosa: "سموسہ",
  daal: "دال",
  gosht: "گوشت",
  qeema: "قیمہ",
  murghi: "مرغی",
  machhli: "مچھلی",
  anda: "انڈا",
  sabzi: "سبزی",
  maida: "میدہ",
  namak: "نمک",
  cheeni: "چینی",
  mirch: "مرچ",
  haldi: "ہلدی",
  dhaniya: "دھنیا",
  zeera: "زیرہ",
  lehsun: "لہسن",
  adrak: "ادرک",
  pyaz: "پیاز",
  tamatar: "ٹماٹر",
  aloo: "آلو",
  gajar: "گاجر",
  matar: "مٹر",
  palak: "پالک",
  gobhi: "گوبھی",
  bhindi: "بھنڈی",
  karela: "کریلا",
  kaddu: "کدو",
  kheera: "کھیرا",
  phal: "پھل",
  saib: "سیب",
  kela: "کیلا",
  angoor: "انگور",
  anar: "انار",
  amrood: "امرود",
  tarbooz: "تربوز",
  badam: "بادام",
  pista: "پستہ",
  kaju: "کاجو",
  kheer: "کھیر",
  halwa: "حلوہ",
  zarda: "زردہ",
  jalebi: "جلیبی",
  kulfi: "کلفی",
  cake: "کیک",
  chocolate: "چاکلیٹ",
  mithai: "مٹھائی",
  salad: "سلاد",
  raita: "رائتہ",
  chatni: "چٹنی",
  achaar: "اچار",
  dimagh: "دماغ",
  jism: "جسم",
  sar: "سر",
  aankh: "آنکھ",
  aankhein: "آنکھیں",
  kaan: "کان",
  naak: "ناک",
  munh: "منہ",
  daant: "دانت",
  zuban: "زبان",
  gala: "گلا",
  gardan: "گردن",
  kandha: "کندھا",
  bazu: "بازو",
  hath: "ہاتھ",
  ungli: "انگلی",
  seena: "سینہ",
  pet: "پیٹ",
  kamar: "کمر",
  taang: "ٹانگ",
  paon: "پاؤں",
  haddi: "ہڈی",
  khoon: "خون",
  jild: "جلد",
  baal: "بال",
  chehra: "چہرہ",
  sehat: "صحت",
  beemari: "بیماری",
  dard: "درد",
  ilaj: "علاج",
  dawa: "دوا",
  goli: "گولی",
  ishq: "عشق",
  nafrat: "نفرت",
  gham: "غم",
  dukh: "دکھ",
  ummeed: "امید",
  yaqeen: "یقین",
  sach: "سچ",
  jhoot: "جھوٹ",
  insaf: "انصاف",
  zulm: "ظلم",
  izzat: "عزت",
  dua: "دعا",
  khwab: "خواب",
  khayal: "خیال",
  maut: "موت",
  taqat: "طقت",
  koshish: "کوشش",
  kamyabi: "کامیابی",
  nakami: "ناکامی",
  faisla: "فیصلہ",
  ghussa: "غصہ",
  akhlaq: "اخلاق",
  wafa: "وفا",
  dosti: "دوستی",
  sakoon: "سکون",
  ilm: "علم",
  taleem: "تعلیم",
  sabaq: "سبق",
  imtihan: "امتحان",
  masla: "مسئلہ",
  sawal: "سوال",
  jawab: "جواب",
  lafz: "لفظ",
  jumla: "جملہ",
  maani: "معنی",
  matlab: "مطلب",
  khabar: "خبر",
  akhbar: "اخبار",
  kahani: "کہانی",
  qissa: "قصہ",
  dastan: "داستان",
  novel: "ناول",
  mazmoon: "مضمون",
  ghazal: "غزل",
  nazm: "نظم",
  adab: "ادب",
  tareekh: "تاریخ",
  science: "سائنس",
  falsafa: "فلسفہ",
  saqafat: "ثقافت",
  tehzeeb: "تہذیب",
  moseeqi: "موسیقی",
  tasweer: "تصویر",
  rang: "رنگ",
  awaz: "آواز",
  khamoshi: "خاموشی",
  namaz: "نماز",
  roza: "روزہ",
  hajj: "حج",
  eid: "عید",
  qurbani: "قربانی",
  mazhab: "مذہب",
  rasool: "رسول",
  nabi: "نبی",
  quran: "قرآن",
  hadees: "حدیث",
  deen: "دین",
  imaan: "ایمان",
  jannat: "جنت",
  dozakh: "دوزخ",
  farishta: "فرشتہ",
  qayamat: "قیامت",
  ibadat: "عبادت",
  gunah: "گناہ",
  rehmat: "رحمت",
  barkat: "برکت",
  naiki: "نیکی",
  siyasat: "سیاست",
  election: "الیکشن",
  intikhab: "انتخاب",
  vote: "ووٹ",
  party: "پارٹی",
  jamaat: "جماعت",
  qanoon: "قانون",
  dastoor: "دستور",
  hukumat: "حکومت",
  jurm: "جرم",
  mujrim: "مجرم",
  saza: "سزا",
  fauj: "فوج",
  jang: "جنگ",
  larai: "لڑائی",
  aman: "امن",
  sulah: "صلح",
  tijarat: "تجارت",
  karobar: "کاروبار",
  sanat: "صنعت",
  taraqqi: "ترقی",
  report: "رپورٹ",
  conference: "کانفرنس",
  taqreer: "تقریر",
  aasman: "آسمان",
  zameen: "زمین",
  sooraj: "سورج",
  sitara: "ستارہ",
  pahaar: "پہاڑ",
  nadi: "ندی",
  darya: "دریا",
  samundar: "سمندر",
  jungle: "جنگل",
  bagh: "باغ",
  phool: "پھول",
  darakht: "درخت",
  pattay: "پتّے",
  machli: "مچھلی",
  parinday: "پرندے",
  kutta: "کتا",
  billi: "بلی",
  ghora: "گھوڑا",
  gaay: "گائے",
  bhains: "بھیس",
  bakra: "بکرا",
  mendha: "میڈھا",
  khargosh: "خرگوش",
  saanp: "سانپ",
  cheel: "چیل",
  kabootar: "کبوتر",
  tota: "توتا",
  ullu: "الو",
  chuha: "چوہا"
};
